import { useEffect, useState } from "react";
import { Gauge, HardDrive, Users, MapPin, Loader2, Sparkles, Video, Crown, AlertTriangle } from "lucide-react";
import CockpitLayout from "@/components/admin/CockpitLayout";
import { api } from "@/lib/api";

const GOLD = "#D89A00", INK = "#2C2E33", SLATE = "#777F88", MUTED = "#5B6472";
const HERO = { background: "radial-gradient(120% 140% at 85% 0%, rgba(216,154,0,.22), transparent 55%), var(--f-navy)", borderRadius: 16, padding: "24px 28px", color: "#F7F5F0" };

const fmtBytes = (b) => {
  if (!b) return "0 MB";
  const mb = b / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
};

const STATUS = {
  NORMAL: { color: "#1E7E45", bg: "rgba(30,126,69,.10)", label: "Normal" },
  WARNING: { color: "#B7791F", bg: "rgba(216,154,0,.12)", label: "Achtung" },
  CRITICAL: { color: "#C2410C", bg: "rgba(194,65,12,.10)", label: "Kritisch" },
  BLOCKED: { color: "#DC2626", bg: "rgba(220,38,38,.10)", label: "Voll" },
};

export default function PlanNutzung() {
  const [u, setU] = useState(null);

  useEffect(() => { api.get("/usage").then(({ data }) => setU(data)).catch(() => {}); }, []);

  if (!u) return <CockpitLayout active="plan" title="Plan & Nutzung"><div className="flex justify-center py-20"><Loader2 className="animate-spin" style={{ color: GOLD }} /></div></CockpitLayout>;

  const st = STATUS[u.storage.status] || STATUS.NORMAL;
  const pct = Math.min(100, u.storage.percent || 0);

  const stat = (icon, label, value, sub) => (
    <div className="fundio-panel">
      <div className="flex items-center gap-2 text-[13px] font-semibold uppercase tracking-wide" style={{ color: SLATE }}>{icon}{label}</div>
      <p className="mt-2 text-[24px] font-black tabular-nums" style={{ color: INK }}>{value}</p>
      {sub && <p className="text-[13px]" style={{ color: MUTED }}>{sub}</p>}
    </div>
  );

  return (
    <CockpitLayout active="plan" title="Plan & Nutzung">
      <div className="fundio-page-stack" data-testid="gold-plan">
        <div className="fundio-section-head">
          <div>
            <h1 className="fundio-page-title flex items-center gap-2.5" data-testid="plan-title">
              <Gauge size={28} style={{ color: GOLD }} /> Plan & Nutzung
            </h1>
            <p className="mt-1 fundio-muted text-[14px]">Dein aktuelles FUNDIO-Paket und deine Speichernutzung.</p>
          </div>
        </div>

        {/* plan hero */}
        <div className="flex flex-wrap items-center justify-between gap-4" style={HERO} data-testid="plan-hero">
          <div>
            <div className="flex items-center gap-2">
              <Crown size={18} style={{ color: GOLD }} />
              <span className="text-[22px] font-black" data-testid="plan-name">{u.plan}</span>
            </div>
            <p className="mt-1 text-[14px]" style={{ color: "#C9CCD2" }} data-testid="plan-price">
              CHF {u.price_monthly_chf}.- / Monat
            </p>
            {u.trial_active && u.trial_ends_at && (
              <span className="mt-2 inline-block rounded-full px-2.5 py-1 text-[11px] font-bold" style={{ background: "rgba(216,154,0,.2)", color: GOLD }} data-testid="plan-trial">
                Testphase bis {new Date(u.trial_ends_at).toLocaleDateString("de-CH")}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-semibold" style={{ background: "rgba(255,255,255,.08)", color: u.features.cockpit ? GOLD : "#7A7F87" }}>Cockpit {u.features.cockpit ? "✓" : "—"}</span>
            <span className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-semibold" style={{ background: "rgba(255,255,255,.08)", color: "#C9CCD2" }}><Video size={13} /> Video: Add-on</span>
            <span className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-semibold" style={{ background: "rgba(255,255,255,.08)", color: "#C9CCD2" }}><Sparkles size={13} /> KI: Add-on</span>
          </div>
        </div>

        {/* storage */}
        <div className="fundio-panel" data-testid="storage-card">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="flex items-center gap-2 text-[17px] font-bold" style={{ color: INK }}><HardDrive size={17} style={{ color: GOLD }} /> Speicher</h2>
            <span className="rounded-full px-2.5 py-1 text-[11px] font-bold uppercase" style={{ background: st.bg, color: st.color }} data-testid="storage-status">{st.label}</span>
          </div>
          <div className="mt-3 flex items-end justify-between">
            <p className="text-[15px] font-semibold" style={{ color: INK }} data-testid="storage-used">{fmtBytes(u.storage.used_bytes)} / {fmtBytes(u.storage.quota_bytes)}</p>
            <p className="text-[20px] font-black tabular-nums" style={{ color: st.color }} data-testid="storage-percent">{pct}%</p>
          </div>
          <div className="mt-2 h-3 overflow-hidden rounded-full" style={{ background: "#EFEDE6" }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: st.color }} />
          </div>
          {u.storage.status === "WARNING" && <p className="mt-3 flex items-center gap-1.5 text-[13px]" style={{ color: "#B7791F" }} data-testid="storage-hint-warning"><AlertTriangle size={14} /> Ihr Speicher füllt sich. Prüfen Sie nicht benötigte Medien.</p>}
          {u.storage.status === "CRITICAL" && <p className="mt-3 flex items-center gap-1.5 text-[13px] font-semibold" style={{ color: "#C2410C" }} data-testid="storage-hint-critical"><AlertTriangle size={14} /> Ihr Speicher ist fast voll. Löschen Sie Medien oder erweitern Sie Ihren Speicher (+5 GB CHF 5.-/Monat).</p>}
          {u.storage.status === "BLOCKED" && <p className="mt-3 flex items-center gap-1.5 text-[13px] font-semibold" style={{ color: "#DC2626" }} data-testid="storage-hint-blocked"><AlertTriangle size={14} /> Ihr Speicher ist ausgeschöpft. Neue Uploads sind blockiert. Löschen Sie Medien oder erweitern Sie Ihren Speicher.</p>}
        </div>

        {/* counters */}
        <div className="grid gap-4 sm:grid-cols-3">
          {stat(<HardDrive size={14} />, "Medien", u.storage.media_count, "Bilder & Dateien")}
          {stat(<Users size={14} />, "Benutzer", `${u.users.used} / ${u.users.included}`, "inklusive")}
          {stat(<MapPin size={14} />, "Standorte", `${u.locations.used} / ${u.locations.included}`, "inklusive")}
        </div>

        {/* MULTI breakdown */}
        {u.multi && u.location_breakdown && (
          <div className="fundio-panel" data-testid="location-breakdown">
            <h2 className="flex items-center gap-2 text-[17px] font-bold" style={{ color: INK }}><MapPin size={17} style={{ color: GOLD }} /> Speicher pro Standort</h2>
            <p className="mt-1 text-[13px]" style={{ color: SLATE }}>Gemeinsamer 2-GB-Pool der Organisation, aufgeschlüsselt nach Standort.</p>
            <div className="mt-4 space-y-3">
              {u.location_breakdown.map((l) => {
                const p = u.storage.quota_bytes ? Math.min(100, Math.round(l.used_bytes / u.storage.quota_bytes * 100)) : 0;
                return (
                  <div key={l.location_id} data-testid={`breakdown-${l.location_id}`}>
                    <div className="flex items-center justify-between text-[13.5px]">
                      <span className="font-semibold" style={{ color: INK }}>{l.name}{l.city ? ` · ${l.city}` : ""}</span>
                      <span style={{ color: MUTED }}>{fmtBytes(l.used_bytes)} · {l.media_count} Medien</span>
                    </div>
                    <div className="mt-1 h-2 overflow-hidden rounded-full" style={{ background: "#EFEDE6" }}>
                      <div className="h-full rounded-full" style={{ width: `${p}%`, background: GOLD }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* add-ons (prepared) */}
        <div className="fundio-panel" style={{ borderStyle: "dashed" }} data-testid="addons-card">
          <h2 className="text-[15px] font-bold" style={{ color: INK }}>Erweiterungen</h2>
          <div className="mt-3 grid gap-2 text-[13px] sm:grid-cols-2" style={{ color: MUTED }}>
            <span>+5 GB Speicher — CHF {u.addons.extra_storage_5gb_chf}.-/Monat</span>
            <span>+25 GB Speicher — CHF {u.addons.extra_storage_25gb_chf}.-/Monat</span>
            <span>Zusätzlicher Benutzer — CHF {u.addons.extra_user_chf}.-/Monat</span>
            <span>Zusätzlicher MULTI-Standort — CHF {u.addons.additional_multi_location_chf}.-/Monat</span>
          </div>
          <p className="mt-3 text-[12px]" style={{ color: SLATE }}>Add-ons können bald direkt gebucht werden. Keine automatische Nachbelastung.</p>
        </div>
      </div>
    </CockpitLayout>
  );
}
